import { servedOutline, type DeliveryZone, type LatLng } from '@agos/shared'
import { keepMapSized } from '@agos/ui'
import 'leaflet/dist/leaflet.css'
import { MapContainer, Polygon, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import { useEffect } from 'react'

import { hexToRgba, mapView } from './zone-draft'

/**
 * The zone map.
 *
 * Leaflet 1.9, which is what legacy draws with — and the reason that matters is
 * not nostalgia: the 31 circles and polygons in the dumps were drawn against
 * *this* projection and *this* hit-test, and `findZoneForPoint` reimplements the
 * same ray-cast. Rendering them with a different engine would mean re-verifying
 * every stored zone against a different notion of "inside".
 *
 * **Since §9.800 a circle is drawn as the area the server serves**, not as Leaflet's true circle: legacy and the port
 * before it drew the true one, which at Slovakia's latitude is about half again as wide east-west as the flat-degree
 * area `DeliveryZoneService.withinRadius` counts as inside.
 *
 * Ported from legacy `pages/delivery-zones.tsx`.
 */

/*
 * §9.694 - the marker-icon fix that sat here is in `@agos/ui/map` now, beside the order builder's pin:
 * this map draws circles and polygons and has never drawn a marker.
 */

export type DrawMode = 'circle' | 'polygon' | null

/**
 * Turns a map click into a circle's centre or a polygon's next corner while drawing, and otherwise into a choice of the
 * zone under it - §9.799, owner O148. A click on a zone reaches the map too: Leaflet's paths bubble their mouse events,
 * so one handler sees every click whether or not a shape was under it.
 */
function DrawHandler({
  drawMode,
  onPoint,
  onSelectAt,
}: {
  drawMode: DrawMode
  onPoint: (point: LatLng) => void
  onSelectAt: (point: LatLng) => void
}) {
  const map = useMapEvents({
    click(event) {
      const point = { lat: event.latlng.lat, lng: event.latlng.lng }
      if (drawMode) {
        onPoint(point)
        return
      }
      onSelectAt(point)
    },
  })

  useEffect(() => {
    // The cursor is the only thing telling a manager the map is armed.
    map.getContainer().style.cursor = drawMode ? 'crosshair' : ''
  }, [drawMode, map])

  return null
}

/**
 * Re-measures the map whenever its box changes - §9.692, FB-1002.
 *
 * On a phone the map's pane is `hidden` until the *Mapa* tab is chosen, so Leaflet is created in a
 * 0 x 0 box and nothing told it otherwise: the map stayed grey. `keepMapSized` watches the container.
 */
function KeepSized() {
  const map = useMap()
  useEffect(() => keepMapSized(map), [map])
  return null
}

export interface ZoneMapProps {
  zones: DeliveryZone[]
  /**
   * The franchise's depot, from `GET /delivery-zones/depot` - §9.692.
   *
   * Where the map opens when no zone is drawn yet. `null` while the read is in flight, when it failed,
   * and when the depot cannot be geocoded - all three open on the whole of Slovakia.
   */
  depot: LatLng | null
  editingId: string | null
  drawMode: DrawMode
  onPoint: (point: LatLng) => void
  /**
   * A click while not drawing - §9.799. Required rather than optional: a caller that dropped it would compile and
   * leave the map unable to choose a zone, which is the defect this exists to end (§9.480's reason).
   */
  onSelectAt: (point: LatLng) => void
}

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION = '© OpenStreetMap'

export function ZoneMap({ zones, depot, editingId, drawMode, onPoint, onSelectAt }: ZoneMapProps) {
  const view = mapView(zones, depot)

  return (
    <MapContainer
      center={[view.centre.lat, view.centre.lng]}
      zoom={view.zoom}
      // §9.692 - **a stacking context of its own.** Leaflet puts its panes at z-index 400-700 and its
      // controls at 1000, so a container that is not one lets the zones and the zoom buttons paint over
      // the sidebar drawer and the header's menus. Legacy had `z-0` (`delivery-zones.tsx:531`) and the
      // port dropped it; `isolate` says the same without depending on Leaflet having positioned the box.
      className="w-full h-full isolate z-0"
      // Recreated when the view changes, which is how the map follows a zone that was just drawn - and
      // a depot that arrives after the zones - without fighting the user's own panning.
      key={`${view.centre.lat},${view.centre.lng},${view.zoom}`}
    >
      <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
      <KeepSized />
      <DrawHandler drawMode={drawMode} onPoint={onPoint} onSelectAt={onSelectAt} />

      {zones.map((zone) => {
        // A disabled zone is still drawn, faintly: a manager needs to see where
        // it was before deciding to switch it back on.
        const opacity = zone.enabled ? 1 : 0.35
        const style = {
          color: zone.color,
          weight: zone.id === editingId ? 4 : 2,
          fillColor: zone.color,
          fillOpacity: (zone.id === editingId ? 0.3 : 0.15) * opacity,
          opacity,
        }

        // §9.800, owner O148 - a circle is drawn as the area the server serves (`servedOutline`, flat degrees), not as
        // Leaflet's true circle, which was about a third wider east-west than what an order to that spot is counted
        // inside. The customer's *Oblasti doručenia* draws the same outline.
        const outline = servedOutline(zone)
        if (outline.length < 3) return null
        return (
          <Polygon
            key={zone.id}
            positions={outline.map((point) => [point.lat, point.lng])}
            pathOptions={zone.type === 'circle' ? { ...style, fillColor: hexToRgba(zone.color, 1) } : style}
          />
        )
      })}
    </MapContainer>
  )
}
